
import { DataSensitivityEnum } from 'src/app/vendor-management/models/data-sensitivity.enum';
import { FrameworkChip } from "src/app/data-types/models/frameworks.interface";
import { AssessmentTemplateEnum } from '../assessments/assessment.enum';
import { AssessmentPage } from 'src/app/ai-assessments/models/assessments.interface';
import { AssessmentResourceId, AssessmentTag } from '../assessments/assessment.interface';

export interface RiskRegistryFilters {
    mitigations?: string,
    inherentRisk?: string[],
    residualRisk?: string[],
    dataSources?: string,
    riskTypes?: string[],
    assessmentTypes?: string,
    /** Assessment-scoped labels → `relations.tags`. */
    labels?: string[],
    /** DevRiskLabels — risk-owned labels → `relations.riskTags`. */
    riskLabels?: string[],
}

export enum RiskRegistryFilteringCategoryEnum {
    All = "All",
    High = "High",
    Medium = "Medium",
    Low = "Low",
    VeryLow = "VeryLow",
    VeryHigh = "VeryHigh",
    Missing = "Missing"
}

export enum RiskRegistryColumnEnum {
    Risk = "name",
    Assessment = "relatedAssessmentId",
    AssessmentType = "assessmentType",
    InherentRisk = "inherentRisk",
    ResidualRisk = "residualRisk",
    Mitigations = "mitigations",
    DataSources = "relatedEntityIds",
    Created = "createdDate",
    Collaborators = "collaborators",
    Likelihood = "likelihood",
    Severity = "severity",
    Accepted = "accepted",
    Comment = "comment",
    Labels = "labels"
}

export enum RiskHelperType {
    ExternalBreach = 'externalBreach',
    InternalBreach = 'internalBreach',
    ExcessiveDataCollection = 'excessiveDataCollection',
    DataMisclassification = 'dataMisclassification',
    ContractualBreach = 'contractualBreach',
    Overcollection = 'overcollection'
}

export interface RiskCatalogsResponse {
    customCatalogRisks: { [type: string] : RiskCatalog };
    predefinedCatalogRisks: { [type: string] : RiskCatalog };
}

export interface RiskCatalog {
    id?: number;
    name: string;
    type: string;
    isHidden: boolean;
    isDefault: boolean;
    assessmentTypes?: AssessmentTemplateEnum[];
    /** DevRiskLabels — catalog labels. Predefined risks the company never edited carry Squidex names with `id: null`. */
    tags?: AssessmentTag[];
    /** DevRiskLabels — custom risks only; the BE rejects it on predefined risks. */
    description?: string;
}

/** Write shape of a catalog risk (DevRiskLabels): `tags` are sent as `tagIds` and always replace the whole list. */
export interface RiskCatalogRequest extends Omit<RiskCatalog, 'tags'> {
    tagIds?: AssessmentResourceId[];
}

export enum RiskRateEnum {
    VeryLow = 'VeryLow',
    Low = 'Low',
    Medium = 'Medium',
    High = 'High',
    VeryHigh = 'VeryHigh'
} 

export interface RiskResponse {
    id?: number;
    name: string;
    type: string;
    createdDate?: Date;
    updatedDate?: Date;
    assessmentPageId?: AssessmentResourceId;
    assessmentPage?: Partial<AssessmentPage> | AssessmentPage;
    accepted: boolean;
    likelihood: RiskRateEnum;
    severity: RiskRateEnum;
    inherentRisk: RiskRateEnum;
    residualRisk: RiskRateEnum;
    mitigations?: string[];
    comment?: string;
    isDefault?: boolean;
    /** DevRiskLabels — risk-owned labels as returned by the BE: [{ id, name }]. */
    tags?: AssessmentTag[];
    /** DevRiskLabels — catalog description echoed on the instance. */
    description?: string;
    /** DevRiskLabels — FE working copy of the label ids (panel form / Risks question rows); derived from `tags` when absent. */
    labels?: AssessmentResourceId[];
    /** DevFlagRisk — ids of the questions that flagged this risk; written as a whole-list `/sourceQuestionIds` replace op. */
    sourceQuestionIds?: AssessmentResourceId[];
}

export enum RiskRateWeight {
    VeryLow = 1,
    Low = 2,
    Medium = 3,
    High = 4,
    VeryHigh = 5
}

export class RiskHelper {
    title: string;
    subTitle?: string;
    id: string;
    bullets?: string;
    calculate?: RiskHelperCalculation;
    schemaId?: string;
}

export class RiskHelperCalculation {
    explanation: string;
    likelihood: string;
    severity: string;
    mitigation: string;
    tableHeaders?: { name: string }[];
}

interface BreachContext {
    system: string;
    name: string;
    logo: string;
    sesitivity: DataSensitivityEnum;
}

export enum RiskRegistryFilteringGroupEnum {
    DataSource = "dataSource",
    RiskType = "riskType",
    Mitigation = "mitigation",
    ResidualRisk = "residualRisk",
    InherentRisk = "inherentRisk",
    AssessmentType = "assessmentType",
    /** Assessment-scoped labels. */
    Labels = "labels",
    /** DevRiskLabels — the risk's own labels. */
    RiskLabels = "riskLabels"
}

export interface ExternalBreachContext extends BreachContext {
    rating: string;
    spinner: boolean;
 }

 export interface InternalBreachContext extends BreachContext {
    employees: number;
 }

 interface DataTypesContext {
    dataType: string;
    frameworks: FrameworkChip[];
}

 export interface OverallocationContext extends DataTypesContext {
    records: number;
 }

 export interface DataMisclassificationContext extends DataTypesContext {
    systems: { name: string, logo: string }[];
 }

export declare type RiskCalculationContext = ExternalBreachContext | InternalBreachContext | OverallocationContext | DataMisclassificationContext;